<template>
  <el-dialog 
    v-model="visible" 
    title="매입처 관리" 
    :width="dialogWidth" 
    :top="`5vh`"
    :style="{ maxHeight: '100vh', overflow: 'auto' }"  
    @close="handleClose">
    
    <!-- 기본정보 섹션 (상단 고정) -->
    <el-form :model="localSupplier" :rules="rules" ref="formRef" :label-width="formLabelWidth" class="base-info-form">
      <el-form-item label="거래처명" prop="name">
        <el-input v-model="localSupplier.name" placeholder="거래처명을 입력하세요" />
      </el-form-item>
      <!-- 개인(프리랜서) 구분 (2026-07-20) — 재단사·가봉사 등. 지결에서 원천징수 3.3% 판정에 사용 -->
      <el-form-item label="개인(프리랜서)" prop="is_individual">
        <el-switch v-model="localSupplier.is_individual" />
        <span class="individual-hint">사업자 없는 개인 — 지결 작성 시 원천징수(3.3%) 확인 경고가 표시됩니다</span>
      </el-form-item>
      <el-form-item label="사업자등록번호" prop="business_number">
        <el-input 
          v-model="localSupplier.business_number" 
          :formatter="formatBusinessNumber"
          :parser="parseBusinessNumber"
          placeholder="사업자등록번호를 입력하세요" 
          @blur="checkDuplicateBusinessNumber"
        />
      </el-form-item>
      <el-form-item label="대표자명" prop="representative_name">
        <el-input v-model="localSupplier.representative_name" placeholder="대표자명을 입력하세요" />
      </el-form-item>
      <el-form-item label="매입처구분" prop="category">
        <el-select v-model="localSupplier.category" placeholder="매입처구분선택">
          <el-option
            v-for="category in categories"
            :key="category.id"  
            :label="category.name"
            :value="category.id"
          />
        </el-select>
      </el-form-item>
  <!--
      <el-form-item label="영문명" prop="english_name">
        <el-input v-model="localSupplier.english_name" />
      </el-form-item>
      <el-form-item label="국가" prop="country">
        <el-input v-model="localSupplier.country" />
      </el-form-item>
      
      <el-form-item label="영문국가명" prop="english_country">
        <el-input v-model="localSupplier.english_country" />
      </el-form-item>
      <el-form-item label="주소" prop="address">
        <el-input v-model="localSupplier.address" />
      </el-form-item>
      <el-form-item label="영문주소" prop="english_address">
        <el-input v-model="localSupplier.english_address" />
      </el-form-item>     
      -->
      <div class="form-footer">
        <el-button v-if="isSupplierManager" type="primary" @click="save">저장</el-button>
      </div>
    </el-form>


    <!-- 하단 탭: 계좌정보 & 연락처정보 -->
    <el-tabs v-model="activeTab" class="bottom-tabs">
      <el-tab-pane label="계좌정보" name="accounts">
        <AccountGrid :supplierId="supplierId" />
      </el-tab-pane>
      <el-tab-pane label="연락처정보" name="contacts">
        <ContactGrid :supplierId="supplierId"  />
      </el-tab-pane>

      <el-tab-pane label="지출결의서" name="expenses">
        <ag-grid-vue
          class="ag-theme-alpine"
          style="width: 100%; height: 300px"
          :rowData="expenses"
          :columnDefs="expenseColumnDefs"
          :defaultColDef="defaultColDef"
          rowSelection="single"
          @rowClicked="onExpenseRowClick"
        />
      </el-tab-pane>
    </el-tabs>

     <!-- 닫기 버튼은 유지 -->
    <template #footer>
      <el-button @click="handleClose">닫기</el-button>
    </template>

  </el-dialog>
      
</template>


<script setup>
  
import { ref ,watch, onMounted ,computed } from "vue";
import AccountGrid from "@/components/AccountGrid.vue";
import ContactGrid from "@/components/ContactGrid.vue";
import axiosInstance from "@/api/axios";
// import { useCodeStore } from "@/stores/codeStore";
import {formatBusinessNumber} from "@/utils/formatBusinessNumber"

import { useUserStore } from "@/stores/user";
import { ElMessage } from "element-plus";
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const expenses = ref([]);
const expenseColumnDefs = ref([
  { field: "document_number", headerName: "지출번호", width: 130 },
  { field: "payment_due_date", headerName: "지급요청일", width: 130 },
  {field:"amount",
      headerName:" 원화 금액",
      valueFormatter: (params) => {
            if (params.value != null) {
                return new Intl.NumberFormat('ko-KR', {
                    style: 'currency',
                    currency: 'KRW'
                }).format(params.value);
            }
            return '-';
        },
    },
  { field: "payment_method_name", headerName: "결제조건", width: 120 },
  { field: "category_name", headerName: "비용구분", width: 120 },
  { field: "our_compay_name", headerName: "발주처", width: 120 },
  {
      headerName: "상태",
      field: "status",
      cellRenderer: (params) => {
        const status = statusStyles[params.value];
        return `<span style="color: ${status.color}; font-weight: bold;">
                  ${status.icon} ${status.label}
                </span>`;
      }
    },
  { field: "created_by_name", headerName: "작성자", width: 100 },
]);
const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
};


const dialogWidth = computed(() => window.innerWidth < 768 ? "60%" : "40%");
const formLabelWidth = computed(() => window.innerWidth < 768 ? "80px" : "120px");

const props = defineProps({
    modelValue: Boolean,
    supplier: { type: Object, default: () => ({}) },
});

const userStore = useUserStore();
const isSupplierManager = userStore.isSupplierManager;



const emit = defineEmits(["update:modelValue", "save", "close"]);
// 로컬 상태로 복사
const localSupplier = ref({ is_individual: false, ...props.supplier });
const visible = ref(true);
const activeTab = ref("baseInfo");
const categories = ref([]);

const isSaved = ref(true); // 저장 여부 추적
const supplierId = ref(null);  // supplierId를 ref로 선언


const rules = {
name: [{ required: true, message: "거래체명을 입력하세요", trigger: "blur" },
        { max: 50, message: '50자 이하로 입력하세요.', trigger: 'blur' },
],
business_number: [
  { required: true, message: "사업자 번호를 입력하세요", trigger: "blur" },
  {      
    pattern: /^\d{10}$/,
    message: "사업자등록번호는 숫자 10자리여야 합니다.",
    trigger: "blur",      
  },
],
representative_name:[
  { required: true, message: "대표자명을 입력하세요", trigger: "blur" },
  { max: 12, message: ' 30자 이하로 입력하세요.', trigger: 'blur' },
],
category:[
{ required: true, message: "매입처구분을 입력하세요", trigger: "blur" }
]
};


// 중복 여부 확인 함수
const checkDuplicateBusinessNumber = async () => {
if (!localSupplier.value.business_number) return; // 입력값이 없으면 체크 안함

try {
const response = await axiosInstance.get("suppliers/", {
  params: { business_number: localSupplier.value.business_number }
});

if (response.data.results.length > 0 && response.data.results[0].supplier_id !== localSupplier.value.supplier_id) {
  ElMessage.error("이미 존재하는 사업자등록번호입니다.");
  localSupplier.value.business_number = ""; // 중복이면 초기화
}
} catch (error) {
console.error("사업자등록번호 중복 확인 중 오류 발생:", error);
}
};


// 파서: 내부 데이터로 저장할 형식
const parseBusinessNumber = (value) => {
return value.replace(/\D/g, ""); // 숫자만 추출
};


//매입처 카데고리 가져오기
const fetchCategories = async () => {
  try {
    const response = await axiosInstance.get("suppliercategory/");
    // SupplierCategoryViewSet은 pagination_class=None 이라 plain 배열로 내려옴
    // 안전하게 두 형식(paginated / plain) 모두 처리
    const data = Array.isArray(response.data)
      ? response.data
      : (response.data?.results ?? []);

    categories.value = data
      .filter(cat => cat && cat.id !== undefined && cat.id !== null)
      .map(cat => ({
        id: Number(cat.id),  // 숫자로 변환
        name: cat.name
      }));
    console.log(categories.value);
  } catch (error) {
    console.error("매입처구분 조회 실패:", error);
    categories.value = [];
  }
};

const fetchExpenses = async () => {
  if (!supplierId.value) return;
  try {
    const response = await axiosInstance.get("supplier-expenses/", {
      params: { supplier_id: supplierId.value },
    });
    expenses.value = response.data;
    console.log(expenseColumnDefs.value)
  } catch (err) {
    console.error("지출결의서 목록 조회 실패:", err);
  }
};


// props가 변경되면 로컬 상태를 동기화
watch(
() => props.supplier,
(newValue) => {
  localSupplier.value = { ...newValue };

}
);

// 1. 데이터 변경 감지
watch(localSupplier, () => {
      isSaved.value = false; // 데이터 변경 시 저장되지 않음 상태로 변경
      console.log("localSupplier changed")
    }, { deep: true }
);

watch(() => localSupplier.value.supplier_id, (newSupplierId) => {
  console.log("SupplierDialog - supplierId 변경 감지:", newSupplierId);
  supplierId.value = newSupplierId;
});

// props가 변경되면 로컬 상태를 동기화
watch(
() => props.modelValue,
(newValue) => {
  visible.value = newValue;
  console.log("SupplierDialog.vue - props.modelValue changed:", visible.value);
}
);

// 닫기 버튼 클릭 시 처리
const handleClose = () => {
  emit("close"); // 부모에게 닫기 이벤트 전달
  emit("update:modelValue", false); // 다이얼로그 닫기

};


//저장
const save = async () => {
  try {

    let response;

    console.log(" SupplierDialog.vue save 호출", localSupplier.value)
    if (localSupplier.value.supplier_id) {
      // 수정 요청
      response =  await axiosInstance.put(`suppliers/${localSupplier.value.supplier_id}/`, {
        ...localSupplier.value,
      });


    } else {
      // 새 매입처 추가 요청
      response = await axiosInstance.post("suppliers/", {
        ...localSupplier.value,
      });
      localSupplier.value.supplier_id = response.data.supplier_id;//새로 생성된 ID설정 
    }
    supplierId.value = localSupplier.value.supplier_id; // supplierId 동기화

    emit("save",localSupplier.value)
    isSaved.value=true

    syncSupplierId();

    // 저장 피드백 (2026-08-01, 사이먼: "추가해도 반응이 없다") —
    // 다이얼로그는 계좌·연락처 이어서 입력하도록 열어두되, 결과는 명확히 알림
    ElMessage.success(
      response.config?.method === 'post'
        ? `매입처 "${localSupplier.value.name}" 등록 완료 — 계좌·연락처를 이어서 입력할 수 있습니다.`
        : '매입처 정보를 저장했습니다.'
    );

  } catch (error) {
    console.error("저장 중 오류 발생:", error);
    // 실패도 화면에 표시 (기존: console만 찍혀 조용히 실패) — 2026-08-01
    const d = error.response?.data;
    const msg = typeof d === 'string' ? d
      : d?.detail || (d && Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' / '))
      || '저장에 실패했습니다.';
    ElMessage.error(msg);
  }
};

const syncSupplierId = () => {
  supplierId.value = localSupplier.value.supplier_id;
};

onMounted(() => {
  fetchCategories();
  syncSupplierId();
  fetchExpenses();
  console.log("SupplierDialog.vue - mounted");
  console.log(localSupplier.value)
});

const statusStyles = {
    draft: { label: "작성 중", color: "gray", icon: "📝" },
    submitted: { label: "제출 완료", color: "blue", icon: "📤" },
    received: { label: "경리과 접수", color: "orange", icon: "📥" },
    processing: { label: "집행 중", color: "green", icon: "⚙️" },
    completed: { label: "집행 완료", color: "black", icon: "✅" },
  };
  
</script>

<style scoped>
.individual-hint { font-size: 12px; color: #909399; margin-left: 8px; }
/* 기본정보 폼 스타일 */
.base-info-form {
  margin-bottom: 20px; /* 하단 탭과 간격 추가 */
  padding: 10px;
  border-bottom: 1px solid #ddd; /* 구분선 추가 */
}

/* 하단 탭 스타일 */
.bottom-tabs {
  margin-top: 20px;
}
</style>
